import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import logo from "assets/images/logo.svg";
import closeIcon from "assets/images/close-icon.svg";
import { useGetLoggedInUser } from "utils/api/user.api";
import { useLogout } from "utils/api/auth.api";
import {
  getDashboardNavItems,
  getSettingsNavItems,
  type RouteConfig,
} from "routes/navigation.config";

// Constants
const NAV_LINK_CLASS =
  "aria-[current=page]:bg-cyan-800/20 aria-[current=page]:font-bold hover:bg-cyan-800/20 rounded-lg flex items-center text-black-800 text-sm hover:font-bold transition p-1 mb-2";

const ICON_PROPS = {
  width: "21px",
  height: "21px",
  className: "mr-2",
  color: "#333",
} as const;

// Components
const NavMenuItem: React.FC<RouteConfig> = ({ path, label, Icon }) => (
  <li>
    <NavLink to={path} className={NAV_LINK_CLASS}>
      {Icon && <Icon {...ICON_PROPS} />}
      {label}
    </NavLink>
  </li>
);

const Sidebar: React.FC<{
  profile: any;
  onLogout: () => void;
}> = ({ profile, onLogout }) => {
  const dashboardItems = getDashboardNavItems();
  const settingsItems = getSettingsNavItems(profile);

  return (
    <aside className="dashboard-sidebar bg-white border-r border-black-800/20">
      <div className="sidebar-wrapper h-full relative p-6">
        <button className="sidebar-toggler absolute top-3 right-3 block sm:hidden">
          <img src={closeIcon} alt="close" />
        </button>

        <div className="sidebar-top pb-20">
          <div className="flex items-center mb-6">
            <img src={logo} className="mr-2" alt="logo" />
          </div>

          <nav>
            <h5 className="text-black-800/40 text-sm mb-2">Dashboard</h5>
            <ul className="mb-6">
              {dashboardItems.map((item) => (
                <NavMenuItem key={item.path} {...item} />
              ))}
            </ul>

            {settingsItems.length > 0 && (
              <>
                <h5 className="text-black-800/40 text-sm mb-2">Settings</h5>
                <ul className="mb-6">
                  {settingsItems.map((item) => (
                    <NavMenuItem key={item.path} {...item} />
                  ))}
                </ul>
              </>
            )}
          </nav>
        </div>

        <div className="sidebar-bottom w-[15rem] bg-white fixed bottom-0 left-0 right-0 p-6">
          <span
            onClick={onLogout}
            className="cursor-pointer text-black-800 text-sm font-bold"
          >
            Logout
          </span>
          <p className="text-black-800 text-xs">Dashboard v1.0</p>
        </div>
      </div>
    </aside>
  );
};

// Main Layout Component
export default function Layout({
  children,
}: {
  children?: JSX.Element | JSX.Element[];
}) {
  const navigate = useNavigate();
  const { data: profile } = useGetLoggedInUser();
  const { mutate: logout } = useLogout();

  const handleLogout = () => {
    const clearAuthAndNavigate = () => {
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      navigate("/logout");
    };

    logout(undefined, {
      onSuccess: clearAuthAndNavigate,
      onError: clearAuthAndNavigate,
    });
  };

  return (
    <main className="dashboard-section">
      <Sidebar profile={profile} onLogout={handleLogout} />
      {children}
    </main>
  );
}
